import React, { useState, useRef, useEffect } from 'react';

export interface ClampedCellContentProps {
  /** Content to render inside the clamp */
  children: React.ReactNode;
  /** Maximum visible lines before clamping. Default: 3 */
  maxLines?: number;
  /** Text for the expand button. Default: "View more" */
  expandText?: string;
  /** Text for the collapse button. Default: "Show less" */
  collapseText?: string;
  /** Custom className for the toggle button */
  buttonClassName?: string;
}

/**
 * Wraps content with CSS line-clamp. When the content exceeds `maxLines`,
 * a "View more" / "Show less" toggle is shown.
 *
 * @example
 * ```tsx
 * <TableCell>
 *   <ClampedCellContent maxLines={3}>
 *     {longText}
 *   </ClampedCellContent>
 * </TableCell>
 * ```
 */
export function ClampedCellContent({
  children,
  maxLines = 3,
  expandText = 'View more',
  collapseText = 'Show less',
  buttonClassName = 'text-orange-600 hover:text-orange-500 text-xs mt-0.5 font-medium',
}: ClampedCellContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const isExpandedRef = useRef(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || isExpandedRef.current) return;
    setHasOverflow(el.scrollHeight > el.clientHeight + 1);
  }, [children]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isExpandedRef.current;
    isExpandedRef.current = next;
    setIsExpanded(next);
  };

  return (
    <div>
      <div
        ref={contentRef}
        style={!isExpanded ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
          wordBreak: 'break-word',
        } : {
          wordBreak: 'break-word',
        }}
      >
        {children}
      </div>
      {hasOverflow && (
        <button
          onClick={handleToggle}
          className={buttonClassName}
          type="button"
          aria-expanded={isExpanded}
        >
          {isExpanded ? collapseText : expandText}
        </button>
      )}
    </div>
  );
}
